/**
 * Minimal hash-based router with no external deps.
 * Routes: monitor (#/ or #), dashboard (#/dashboard), history (#/history).
 */

import { useEffect, useState } from "react";

export type Route =
  | { name: "dashboard" }
  | { name: "monitor" }
  | { name: "history" }
  | { name: "guide" }
  | { name: "graph" }
  | { name: "insights" }
  | { name: "workbench" }
  | { name: "runs" }
  | { name: "skill"; skillName: string };

function parseHash(): Route {
  const h = window.location.hash.replace(/^#\/?/, "").split("?")[0];
  if (h.startsWith("skill/")) return { name: "skill", skillName: decodeURIComponent(h.slice(6)) };
  if (h === "history") return { name: "history" };
  if (h === "guide") return { name: "guide" };
  if (h === "graph") return { name: "graph" };
  if (h === "insights") return { name: "insights" };
  if (h === "workbench") return { name: "workbench" };
  if (h === "runs") return { name: "runs" };
  if (h === "dashboard") return { name: "dashboard" };
  if (h === "monitor" || h === "") return { name: "monitor" };
  return { name: "monitor" };
}

export function useRoute(): [Route, (r: Route) => void] {
  const [route, setRoute] = useState<Route>(parseHash);
  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const navigate = (r: Route) => {
    if (r.name === "skill") {
      window.location.hash = `/skill/${encodeURIComponent(r.skillName)}`;
      return;
    }
    const target = r.name === "monitor" ? "" : `/${r.name}`;
    window.location.hash = target;
  };
  return [route, navigate];
}

export function pathFor(r: Route): string {
  if (r.name === "skill") return `#/skill/${encodeURIComponent(r.skillName)}`;
  return r.name === "monitor" ? "#" : `#/${r.name}`;
}

/** Navigate to the dedicated skill detail page. */
export function navigateToSkill(skillName: string) {
  window.location.hash = `/skill/${encodeURIComponent(skillName)}`;
}

/** Jump to monitor with optional query params (skill, compare, tiers). */
export function navigateToMonitor(opts?: {
  skill?: string;
  tiers?: string;
  compare?: string;
}) {
  const p = new URLSearchParams(window.location.search);
  if (opts?.skill) p.set("skill", opts.skill);
  else p.delete("skill");
  if (opts?.tiers) p.set("tiers", opts.tiers);
  if (opts?.compare) p.set("compare", opts.compare);
  const qs = p.toString();
  window.location.assign(`${window.location.pathname}${qs ? `?${qs}` : ""}#`);
}
